import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { BarChart3, Clock, Users, Check, Lock, CheckCircle2, Eye, EyeOff, TrendingUp } from "lucide-react";
import { votePoll, closePoll, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import PollAnalyticsDialog from "./PollAnalyticsDialog";

export default function PollCard({ poll, postId, currentUserId, authorId, onVote }) {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [voting, setVoting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [localPoll, setLocalPoll] = useState(poll);
  const [showAnalytics, setShowAnalytics] = useState(false);

  useEffect(() => {
    setLocalPoll(poll);
  }, [poll]);

  const isOwner = currentUserId === authorId;
  const isClosed = localPoll.is_closed;
  const hasVoted = localPoll.user_voted;
  const showResults = localPoll.show_results;
  const totalVotes = localPoll.total_votes || 0;
  const totalVoters = localPoll.total_voters || 0;

  // Calculate time remaining
  const getTimeRemaining = () => {
    if (isClosed) return "Closed";
    const closesAt = new Date(localPoll.closes_at);
    const now = new Date();
    const diff = closesAt - now;
    
    if (diff <= 0) return "Closed";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h left`;
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${mins}m left`;
  };

  const toggleOption = (index) => {
    if (hasVoted || isClosed) return;
    
    if (localPoll.allow_multiple) {
      setSelectedOptions(prev => 
        prev.includes(index) 
          ? prev.filter(i => i !== index)
          : [...prev, index]
      );
    } else {
      setSelectedOptions([index]);
    }
  };

  const handleVote = async () => {
    if (selectedOptions.length === 0) {
      toast.error("Please select at least one option");
      return;
    }
    
    setVoting(true);
    try {
      const res = await votePoll(postId, selectedOptions);
      
      // Update local poll state
      const updatedOptions = localPoll.options.map((opt, i) => ({
        ...opt,
        vote_count: selectedOptions.includes(i) ? (opt.vote_count || 0) + 1 : opt.vote_count
      }));
      
      setLocalPoll({
        ...localPoll,
        options: updatedOptions,
        user_voted: true,
        user_votes: selectedOptions,
        show_results: true,
        total_votes: res.data.total_votes,
        total_voters: res.data.total_voters
      });
      
      toast.success("Your vote has been recorded!");
      if (onVote) onVote();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to vote");
    }
    setVoting(false);
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      await closePoll(postId);
      setLocalPoll({ ...localPoll, is_closed: true, show_results: true });
      toast.success("Poll closed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to close poll");
    }
    setClosing(false);
  };

  const getPercentage = (voteCount) => {
    if (!showResults || totalVotes === 0) return 0;
    return Math.round((voteCount / totalVotes) * 100);
  };

  return (
    <div className="bg-[#F9F7F2] rounded-lg border border-[#E5E0D5] p-4 space-y-4" data-testid={`poll-card-${postId}`}>
      {/* Poll Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[var(--liturgical-primary)]" />
          <h4 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">
            {localPoll.question}
          </h4>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {localPoll.anonymous ? (
            <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-[10px] font-['Nunito']">
              <EyeOff className="w-3 h-3 mr-1" /> Anonymous
            </Badge>
          ) : (
            <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-[10px] font-['Nunito']">
              <Eye className="w-3 h-3 mr-1" /> Public
            </Badge>
          )}
          <Badge 
            className={`text-[10px] font-['Nunito'] ${isClosed ? 'bg-[#6B665E]' : 'bg-[var(--liturgical-primary)]'} text-white`}
          >
            <Clock className="w-3 h-3 mr-1" /> {getTimeRemaining()}
          </Badge>
        </div>
      </div>

      {/* Poll Options */}
      <div className="space-y-2">
        {localPoll.options.map((option, index) => {
          const isSelected = selectedOptions.includes(index);
          const userVoted = localPoll.user_votes?.includes(index);
          const percentage = getPercentage(option.vote_count || 0);
          const isWinning = showResults && percentage > 0 && percentage === Math.max(...localPoll.options.map(o => getPercentage(o.vote_count || 0)));
          
          return (
            <div key={index} className="relative">
              <button
                onClick={() => toggleOption(index)}
                disabled={hasVoted || isClosed}
                className={`w-full p-3 rounded-lg border-2 transition-all text-left relative overflow-hidden
                  ${hasVoted || isClosed 
                    ? 'cursor-default' 
                    : 'cursor-pointer hover:border-[var(--liturgical-primary)]'
                  }
                  ${isSelected && !hasVoted
                    ? 'border-[var(--liturgical-primary)] bg-[var(--liturgical-primary)]/5' 
                    : userVoted
                      ? 'border-[var(--liturgical-primary)] bg-[var(--liturgical-primary)]/10'
                      : 'border-[#E5E0D5] bg-white'
                  }
                `}
                data-testid={`poll-option-${index}`}
              >
                {/* Progress bar background */}
                {showResults && (
                  <div 
                    className={`absolute inset-0 transition-all duration-500 ${isWinning ? 'bg-[var(--liturgical-primary)]/15' : 'bg-[#E5E0D5]/30'}`}
                    style={{ width: `${percentage}%` }}
                  />
                )}
                
                <div className="relative flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {/* Checkbox/Radio indicator */}
                    <div className={`w-5 h-5 rounded-${localPoll.allow_multiple ? 'md' : 'full'} border-2 flex items-center justify-center flex-shrink-0
                      ${isSelected || userVoted 
                        ? 'border-[var(--liturgical-primary)] bg-[var(--liturgical-primary)]' 
                        : 'border-[#C5C0B5]'
                      }`}
                    >
                      {(isSelected || userVoted) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    
                    <span className={`font-['Nunito'] text-sm ${userVoted ? 'font-semibold text-[#2D2A26]' : 'text-[#2D2A26]'}`}>
                      {option.text}
                    </span>
                    
                    {userVoted && (
                      <Badge className="bg-[var(--liturgical-primary)] text-white text-[9px] font-['Nunito']">
                        Your vote
                      </Badge>
                    )}
                  </div>
                  
                  {showResults && (
                    <div className="flex items-center gap-2">
                      <span className={`font-['Nunito'] text-sm font-semibold ${isWinning ? 'text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}>
                        {percentage}%
                      </span>
                      <span className="font-['Nunito'] text-xs text-[#6B665E]">
                        ({option.vote_count || 0})
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Show voters if not anonymous */}
                {showResults && !localPoll.anonymous && option.voters && option.voters.length > 0 && (
                  <div className="relative mt-2 pt-2 border-t border-[#E5E0D5]/50 flex flex-wrap gap-1">
                    {option.voters.slice(0, 5).map((voter) => (
                      <Avatar key={voter.user_id} className="w-6 h-6 border border-white">
                        <AvatarImage src={resolveImageUrl(voter.picture)} />
                        <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[8px]">
                          {voter.name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                    {option.voters.length > 5 && (
                      <span className="text-xs text-[#6B665E] font-['Nunito'] self-center ml-1">
                        +{option.voters.length - 5} more
                      </span>
                    )}
                  </div>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Poll Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-[#E5E0D5]/50">
        <div className="flex items-center gap-3 text-xs text-[#6B665E] font-['Nunito']">
          {showResults && (
            <>
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {totalVoters} {totalVoters === 1 ? 'voter' : 'voters'}
              </span>
              <span>•</span>
              <span>{totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}</span>
            </>
          )}
          {localPoll.allow_multiple && (
            <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-[9px]">
              Multiple choice
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Analytics button (only for owner) */}
          {isOwner && (
            <Button
              onClick={() => setShowAnalytics(true)}
              variant="outline"
              className="border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] font-['Nunito'] text-sm h-8 px-3 hover:bg-[var(--liturgical-primary)]/5"
              data-testid="poll-analytics-btn"
            >
              <TrendingUp className="w-3.5 h-3.5 mr-1" />
              Analytics
            </Button>
          )}
          
          {/* Vote button */}
          {!hasVoted && !isClosed && (
            <Button
              onClick={handleVote}
              disabled={voting || selectedOptions.length === 0}
              className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-sm h-8 px-4"
              data-testid="poll-vote-btn"
            >
              {voting ? "Voting..." : "Vote"}
            </Button>
          )}
          
          {/* Close poll button (only for owner) */}
          {isOwner && !isClosed && (
            <Button
              onClick={handleClose}
              disabled={closing}
              variant="outline"
              className="border-[#E5E0D5] text-[#6B665E] font-['Nunito'] text-sm h-8 px-3"
              data-testid="poll-close-btn"
            >
              <Lock className="w-3.5 h-3.5 mr-1" />
              {closing ? "Closing..." : "Close Poll"}
            </Button>
          )}
          
          {/* Voted indicator */}
          {hasVoted && (
            <div className="flex items-center gap-1 text-xs text-[var(--liturgical-primary)] font-['Nunito']">
              <CheckCircle2 className="w-4 h-4" />
              Voted
            </div>
          )}
        </div>
      </div>
      
      {/* Analytics Dialog */}
      <PollAnalyticsDialog 
        postId={postId} 
        open={showAnalytics} 
        onClose={() => setShowAnalytics(false)} 
      />
    </div>
  );
}
